import { useCallback, useMemo, useState } from 'react';
import { FlatList, Pressable, RefreshControl, Text, View } from 'react-native';
import { useFocusEffect, useRouter } from 'expo-router';
import * as Location from 'expo-location';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../context/AuthContext';
import { nearbyShops } from '../api/shops';
import { NearbyShop, ReviewTask } from '../types';
import { Button, Card, CardSkeleton, EmptyState, ErrorText, GradientCard, IconBadge, ScreenContainer } from '../components/ui';
import { PlatformQuestsSection } from '../components/PlatformQuestsSection';
import { colors, fonts, gradients } from '../theme';
import { getCurrentPositionWithTimeout } from '../utils/location';

interface TaskRow {
  task: ReviewTask;
  shop: NearbyShop;
}

export default function ReviewerHomeScreen() {
  const { token } = useAuth();
  const router = useRouter();
  const [rows, setRows] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        setErrorMsg('Cần quyền vị trí để tìm nhiệm vụ gần bạn');
        setRows([]);
        return;
      }
      const position = await getCurrentPositionWithTimeout({ accuracy: Location.Accuracy.High });
      const shops = await nearbyShops(
        token,
        position.coords.latitude,
        position.coords.longitude,
      );
      const flattened: TaskRow[] = shops.flatMap((shop) =>
        shop.reviewTasks.map((task) => ({ task, shop })),
      );
      setRows(flattened);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Không tải được danh sách nhiệm vụ');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  // VIP perk: one random featured (VIP-shop) task gets a highlighted banner
  // slot up top, re-rolled whenever the list reloads.
  const featuredRow = useMemo(() => {
    const featured = rows.filter((r) => r.shop.isFeatured);
    if (featured.length === 0) return null;
    return featured[Math.floor(Math.random() * featured.length)];
  }, [rows]);

  if (loading && rows.length === 0) {
    return (
      <ScreenContainer safeTop safeBottom>
        <PlatformQuestsSection />
        <Text style={{ fontSize: 22, fontFamily: fonts.extraBold, color: colors.text, marginBottom: 14 }}>
          Nhiệm vụ gần bạn
        </Text>
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer safeTop safeBottom>
      <FlatList
        data={rows}
        keyExtractor={(item) => item.task.id}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={load} />}
        ListEmptyComponent={
          !loading ? (
            errorMsg ? (
              <View style={{ alignItems: 'center', gap: 8 }}>
                <ErrorText>{errorMsg}</ErrorText>
                <Button title="Thử lại" variant="outline" onPress={load} />
              </View>
            ) : (
              <EmptyState text="Chưa có nhiệm vụ nào gần bạn" icon="compass-outline" />
            )
          ) : null
        }
        ListHeaderComponent={
          <>
            <PlatformQuestsSection />
            <Text style={{ fontSize: 22, fontFamily: fonts.extraBold, color: colors.text, marginBottom: 18 }}>
              Nhiệm vụ gần bạn
            </Text>
            {featuredRow ? (
              <Pressable onPress={() => router.push(`/task/${featuredRow.task.id}`)}>
                <GradientCard colors={gradients.hero}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <Ionicons name="star" size={14} color="#fff" />
                    <Text style={{ color: '#fff', fontFamily: fonts.bold, fontSize: 12, letterSpacing: 0.4 }}>
                      ĐƯỢC TÀI TRỢ
                    </Text>
                  </View>
                  <Text style={{ fontSize: 17, fontFamily: fonts.bold, color: '#fff' }}>
                    {featuredRow.shop.name}
                  </Text>
                  <Text style={{ color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>{featuredRow.shop.address}</Text>
                  <Text style={{ color: '#fff', fontFamily: fonts.bold, fontSize: 16, marginTop: 12 }}>
                    {featuredRow.task.rewardCoins} xu / lượt
                  </Text>
                </GradientCard>
              </Pressable>
            ) : null}
          </>
        }
        renderItem={({ item }) => (
          <Pressable onPress={() => router.push(`/task/${item.task.id}`)}>
            <Card style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 14, marginBottom: 16, padding: 20 }}>
              <IconBadge name="storefront-outline" tone={item.shop.isFeatured ? 'warning' : 'primary'} />
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Text style={{ fontSize: 16, fontFamily: fonts.bold, color: colors.text }}>
                    {item.shop.name}
                  </Text>
                  {item.shop.isFeatured ? <Ionicons name="star" size={13} color={colors.warning} /> : null}
                </View>
                <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 13 }}>{item.shop.address}</Text>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                  <Text style={{ color: colors.primary, fontFamily: fonts.semiBold, fontSize: 15 }}>
                    {item.task.rewardCoins} xu / lượt
                  </Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <Ionicons name="location-outline" size={13} color={colors.textMuted} />
                    <Text style={{ color: colors.textMuted, fontSize: 12 }}>
                      {(item.shop.distanceMeters / 1000).toFixed(1)} km
                    </Text>
                  </View>
                </View>
                <Text style={{ color: colors.textMuted, marginTop: 8, fontSize: 12 }}>
                  Còn {item.task.totalSlots - item.task.filledSlots} lượt
                </Text>
              </View>
            </Card>
          </Pressable>
        )}
      />
    </ScreenContainer>
  );
}
